/** First-run platform wizard. Pure helpers so the form checks stay testable. */

export const SETUP_LATER_KEY = 'unibyte.setup.later';
export const SETUP_DRAFT_KEY = 'unibyte.setup.draft';
export const SETUP_COMPLETED_KEY = 'setup_wizard_completed';

export const SETUP_STEPS = [
  'welcome',
  'brand',
  'ai',
  'mail',
  'customer',
  'site',
  'knowledge',
  'connect',
  'invite',
  'done',
] as const;

export type SetupStepId = (typeof SETUP_STEPS)[number];

export interface SetupDraft {
  step: number;
  tenantId: string;
  siteId: string;
  pairingCode: string;
  pairingExpiresAt: string;
  pairingSiteId: string;
}

const EMPTY_DRAFT: SetupDraft = {
  step: 0,
  tenantId: '',
  siteId: '',
  pairingCode: '',
  pairingExpiresAt: '',
  pairingSiteId: '',
};

/**
 * The setup wizard remains available from the navigation, but it should not
 * interrupt the normal post-login landing page anymore.
 */
export function wizardShouldRedirect(_input: {
  platform: boolean;
  pathname: string;
  completed: boolean | null;
  deferred: boolean;
}): boolean {
  return false;
}

export function readWizardCompleted(
  items: ReadonlyArray<{ key: string; value: string }> | undefined,
): boolean | null {
  if (!items) return null;
  const row = items.find((item) => item.key === SETUP_COMPLETED_KEY);
  return row?.value === 'true';
}

export function readSetupDeferred(storage: Pick<Storage, 'getItem'> | null): boolean {
  if (!storage) return false;
  try {
    return storage.getItem(SETUP_LATER_KEY) === '1';
  } catch {
    return false;
  }
}

export function loadSetupDraft(storage: Pick<Storage, 'getItem'> | null): SetupDraft {
  if (!storage) return { ...EMPTY_DRAFT };
  try {
    const raw = storage.getItem(SETUP_DRAFT_KEY);
    if (!raw) return { ...EMPTY_DRAFT };
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return { ...EMPTY_DRAFT };
    const record = parsed as Partial<SetupDraft>;
    const step = typeof record.step === 'number' ? Math.trunc(record.step) : 0;
    return {
      step: step >= 0 && step < SETUP_STEPS.length ? step : 0,
      tenantId: typeof record.tenantId === 'string' ? record.tenantId : '',
      siteId: typeof record.siteId === 'string' ? record.siteId : '',
      pairingCode: typeof record.pairingCode === 'string' ? record.pairingCode : '',
      pairingExpiresAt: typeof record.pairingExpiresAt === 'string' ? record.pairingExpiresAt : '',
      pairingSiteId: typeof record.pairingSiteId === 'string' ? record.pairingSiteId : '',
    };
  } catch {
    return { ...EMPTY_DRAFT };
  }
}

export function planCodeFromName(name: string): string {
  const slug = name
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
  return slug || 'starter';
}

/** Euro string (comma or dot) to integer cents. Empty means 0. Invalid returns null. */
export function eurosToCents(raw: string): number | null {
  const trimmed = raw.trim().replace(',', '.');
  if (!trimmed) return 0;
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const cents = Math.round(Number(trimmed) * 100);
  if (!Number.isSafeInteger(cents) || cents > 100_000_000) return null;
  return cents;
}

/** Localized message when the origin cannot be registered. Null means it is acceptable. */
export function originError(value: string, locale: 'el' | 'en' = 'el'): string | null {
  const text = (el: string, en: string) => (locale === 'en' ? en : el);
  const trimmed = value.trim();
  if (!trimmed) return text('Εισάγετε την προέλευση της ιστοσελίδας.', 'Enter the website origin.');
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return text(
      'Απαιτείται πλήρες URL, για παράδειγμα https://www.example.com.',
      'A complete URL is required, for example https://www.example.com.',
    );
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return text(
      'Η προέλευση πρέπει να χρησιμοποιεί http ή https.',
      'The origin must use http or https.',
    );
  }
  if (url.username || url.password || url.search || url.hash) {
    return text(
      'Μην συμπεριλάβετε στοιχεία σύνδεσης, query ή fragment.',
      'Do not include credentials, a query, or a fragment.',
    );
  }
  if (url.pathname !== '/' && url.pathname !== '') {
    return text(
      'Μην συμπεριλάβετε διαδρομή. Εισάγετε μόνο την προέλευση, για παράδειγμα https://www.example.com.',
      'Do not include a path. Enter only the origin, for example https://www.example.com.',
    );
  }
  if (url.hostname.includes('*'))
    return text('Δεν επιτρέπονται wildcards.', 'Wildcards are not allowed.');
  return null;
}

export function canonicalOrigin(value: string): string {
  return new URL(value.trim()).origin;
}
